import AsyncStorage from '@react-native-async-storage/async-storage';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {Platform} from 'react-native';
import {getAnalytics, logEvent as firebaseLogEvent} from '@react-native-firebase/analytics';
import {AppEventsLogger} from 'react-native-fbsdk-next';
import {useAuth} from './auth';

export type Step = {
  order: number;
  text: string;
  contextName: string;
  onPress?: () => void;
  topInset?: number;
  position?: {x: number; y: number; width: number; height: number};
  screen: string;
  additionalWidth?: number;
  additionalHeight?: number;
  customY?: number;
  customX?: number;
  params?: Readonly<object | undefined>;
  skipOnBackpress?: boolean;
};

type StartOptions = {
  force?: boolean;
};

interface TutorialContextData {
  startTutorial: (contextName: string, options?: StartOptions) => Promise<void>;
  nextStep: () => void;
  prevStep: () => void;
  endTutorial: () => Promise<void>;
  startIndiqueGanheTutorial: (force?: boolean) => Promise<void>;

  registerStep: (step: Step) => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  setSteps: React.Dispatch<React.SetStateAction<Step[]>>;

  getStorage: (contextName: string) => Promise<string | null>;
  hasFinished: (contextName: string) => Promise<boolean>;

  isActive: boolean;
  activeContext: string | null;
  currentStep: number;
  steps: Step[];
  activeSteps: Step[];
  currentActiveStep: Step | null;
  hasMoreSteps: boolean;

  INDIQUE_GANHE_TUTORIAL_STORAGE_KEY: string;
}

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialContext = createContext<TutorialContextData | undefined>(
  undefined,
);

export const TutorialProvider: React.FC<TutorialProviderProps> = ({
  children,
}) => {
  const [steps, setSteps] = useState<Step[]>([]);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [activeContext, setActiveContext] = useState<string | null>(null);

  // Cache para evitar verificações repetidas do AsyncStorage
  const finishedCache = useRef<Record<string, boolean>>({});
  const isInitialized = useRef(false);

  const {isLogged} = useAuth();

  const INDIQUE_GANHE_TUTORIAL_STORAGE_KEY = 'referAndWinModalShown7';

  // Total de steps esperado por contexto (para saber quando mostrar "Finalizar")
  const TOTAL_STEPS_BY_CONTEXT: Record<string, number> = {
    welcome: 11, // orders 0-10
    indique2: 4, // orders 0-3
  };

  // ---------- Computed values (memoizados) ----------
  const activeSteps = useMemo(() => {
    if (!activeContext) {return [];}
    return steps
      .filter(s => s.contextName === activeContext)
      .sort((a, b) => a.order - b.order);
  }, [steps, activeContext]);

  // currentStep agora representa a ORDER do step, não o índice
  const currentActiveStep = useMemo(() => {
    if (!isActive || activeSteps.length === 0) {return null;}
    // Buscar o step pela order, não pelo índice
    return activeSteps.find(s => s.order === currentStep) ?? null;
  }, [isActive, activeSteps, currentStep]);

  // Verifica se há mais steps além do atual (considerando o total esperado)
  const hasMoreSteps = useMemo(() => {
    if (!activeContext) {return false;}
    const totalExpected = TOTAL_STEPS_BY_CONTEXT[activeContext] ?? 0;
    // currentStep já é a order
    return currentStep < totalExpected - 1;
  }, [activeContext, currentStep]);

  // ---------- Storage helpers ----------
  const storageKey = (contextName: string) => `tutorial_${contextName}`;

  const getStorage = useCallback(async (contextName: string) => {
    return AsyncStorage.getItem(storageKey(contextName));
  }, []);

  const setFinished = useCallback(async (contextName: string) => {
    finishedCache.current[contextName] = true;
    await AsyncStorage.setItem(storageKey(contextName), 'finished');
  }, []);

  const hasFinished = useCallback(async (contextName: string) => {
    if (finishedCache.current[contextName] !== undefined) {
      return finishedCache.current[contextName];
    }
    const v = await getStorage(contextName);
    const finished = v === 'finished';
    finishedCache.current[contextName] = finished;
    return finished;
  }, [getStorage]);

  // ---------- Analytics ----------
  const logAnalyticsEvent = useCallback(
    (event: string, params?: Record<string, any>) => {
      const analytics = getAnalytics();
      firebaseLogEvent(analytics, event as any, params);
      if (Platform.OS !== 'web') {
        AppEventsLogger.logEvent(event, params ?? {});
      }
    },
    [],
  );

  // ---------- Steps ----------
  const registerStep = useCallback((step: Step) => {
    setSteps(prev => {
      const exists = prev.find(
        s => s.order === step.order && s.contextName === step.contextName,
      );
      if (exists) {
        // Atualizar posição se mudou
        if (
          exists.position?.x !== step.position?.x ||
          exists.position?.y !== step.position?.y
        ) {
          return prev.map(s =>
            s.order === step.order && s.contextName === step.contextName
              ? {...s, position: step.position}
              : s,
          );
        }
        return prev;
      }

      // Inserir ordenado
      const next = [...prev, step].sort((a, b) => {
        if (a.contextName === b.contextName) {
          return a.order - b.order;
        }
        return a.contextName.localeCompare(b.contextName);
      });
      return next;
    });
  }, []);

  // ---------- Fluxo ----------
  const startTutorial = useCallback(
    async (contextName: string, options: StartOptions = {}) => {
      const {force = false} = options;

      if (isActive && activeContext === contextName && !force) {return;}
      if (!force && (await hasFinished(contextName))) {return;}

      if (force) {
        setSteps(prev => prev.filter(s => s.contextName !== contextName));
      }

      logAnalyticsEvent('tutorial_started', {context: contextName});
      setActiveContext(contextName);
      setCurrentStep(0);
      setIsActive(true);
    },
    [isActive, activeContext, hasFinished, logAnalyticsEvent],
  );

  const endTutorial = useCallback(async () => {
    if (!activeContext) {return;}

    const contextToCheck = activeContext;

    await setFinished(activeContext);
    logAnalyticsEvent('tutorial_finished', {context: activeContext});

    setIsActive(false);
    setCurrentStep(0);
    setActiveContext(null);

    if (contextToCheck === 'indique2') {
      await AsyncStorage.setItem(INDIQUE_GANHE_TUTORIAL_STORAGE_KEY, 'true');
    }
  }, [activeContext, setFinished, logAnalyticsEvent]);

  const nextStep = useCallback(() => {
    if (!isActive || !activeContext) {return;}

    const totalExpected = TOTAL_STEPS_BY_CONTEXT[activeContext] ?? 0;

    if (currentStep < totalExpected - 1) {
      logAnalyticsEvent('tutorial_step_next', {
        context: activeContext,
        stepOrder: currentStep + 1,
      });
      setCurrentStep(prev => prev + 1);
    } else {
      endTutorial();
    }
  }, [
    isActive,
    activeContext,
    currentStep,
    logAnalyticsEvent,
    endTutorial,
  ]);

  const prevStep = useCallback(() => {
    if (!isActive || !activeContext) {return;}
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isActive, activeContext, currentStep]);

  const startIndiqueGanheTutorial = useCallback(
    async (force: boolean = false) => {
      try {
        if (force) {
          await startTutorial('indique2', {force: true});
          return;
        }

        const indiqueFinished = await hasFinished('indique2');
        if (!indiqueFinished && isLogged) {
          await startTutorial('indique2');
        }
      } catch (e) {
        console.error('Erro ao inicializar tutorial:', e);
      }
    },
    [startTutorial, hasFinished, isLogged],
  );

  // auto-start do welcome - verifica AsyncStorage primeiro
  useEffect(() => {
    if (isInitialized.current) {return;}
    isInitialized.current = true;

    /**if (!isLogged) {
      // Verificar AsyncStorage ANTES de iniciar
      hasFinished('welcome')
        .then(finished => {
          if (!finished) {
            setActiveContext('welcome');
            setCurrentStep(0);
            setIsActive(true);
            logAnalyticsEvent('tutorial_started', {context: 'welcome'});
          }
        })
        .catch(console.error);
    }**/
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        startTutorial,
        nextStep,
        startIndiqueGanheTutorial,
        prevStep,
        endTutorial,
        registerStep,
        setSteps,
        setCurrentStep,
        getStorage,
        hasFinished,
        isActive,
        activeContext,
        currentStep,
        steps,
        activeSteps,
        currentActiveStep,
        hasMoreSteps,
        INDIQUE_GANHE_TUTORIAL_STORAGE_KEY,
      }}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = (): TutorialContextData => {
  if (Platform.OS === 'web') {return false as any;}
  const context = useContext(TutorialContext);
  if (!context)
    {throw new Error('useTutorial must be used within a TutorialProvider');}
  return context;
};
